import type { LeveledLogMethod, Logger, LoggerMethods } from '../../../shared';
import { logMethods } from '../../../shared';

/**
 * Some browsers include extra properties on the error object that are not part
 * of the Error interface. This just allows us to access those properties.
 */
interface ErrorWithExtraBrowserProps extends Error {
    fileName?: string;
    sourceURL?: string;
    lineNumber?: number;
    line?: number;
    columnNumber?: number;
    column?: number;
    code?: string;
}

// Bound client-side log POSTs so one broken client (e.g. a stale bundle stuck
// in an error loop) can't flood `/remix-api/log` the way the June 2026 incident
// did (BB-3773). Both guards cover problem-level reports only and reset on a
// full page load.
const DEDUPE_WINDOW_MS = 30_000;
const MAX_PROBLEM_REPORTS_PER_PAGE = 10;
const PROBLEM_METHODS = new Set<keyof LoggerMethods>([
    'emerg',
    'alert',
    'crit',
    'error',
    'warning',
]);
const lastSentByKey = new Map<string, number>();
let problemReportsSent = 0;

const firstStackLine = (meta: unknown[]): string => {
    for (const value of meta) {
        const stack =
            value instanceof Error
                ? value.stack
                : (value as { stack?: unknown } | null)?.stack;
        if (typeof stack === 'string') {
            const frame = stack
                .split('\n')
                .map((line) => line.trim())
                .find((line) => line.startsWith('at '));
            if (frame) return frame;
        }
    }
    return '';
};

/**
 * Decide whether a problem-level report should be POSTed. Drops exact repeats
 * (same level + message + first stack frame) inside a 30s window, then caps the
 * number of problem reports per page load. notice/info/debug telemetry is never
 * throttled.
 */
const shouldSendReport = (
    method: keyof LoggerMethods,
    message: string,
    meta: unknown[],
): boolean => {
    if (!PROBLEM_METHODS.has(method)) return true;
    if (problemReportsSent >= MAX_PROBLEM_REPORTS_PER_PAGE) return false;
    const now = Date.now();
    const key = `${method}\u001f${message}\u001f${firstStackLine(meta)}`;
    const last = lastSentByKey.get(key);
    if (last !== undefined && now - last < DEDUPE_WINDOW_MS) return false;
    lastSentByKey.set(key, now);
    problemReportsSent += 1;
    return true;
};

/**
 * Creates a client-side logger instance for use in Remix applications.
 * This logger sends log messages to the server via the `/remix-api/log` endpoint.
 *
 * The logger supports all standard logging methods (info, warn, error, debug) and
 * automatically handles error objects by extracting their message and stack trace.
 * All logs are sent asynchronously and failures are silently handled to prevent
 * disrupting the application.
 *
 * The best way to get an instance of the logger is to used the `useLogger` hook.
 *
 * @returns {Logger} A logger instance that implements the Logger interface
 *
 * @example
 * // Basic usage
 * logger.info('User logged in', { userId: '123' });
 *
 * // Logging errors
 * try {
 *   // some code that might throw
 * } catch (err) {
 *   logger.error(`Error while submitting email subscription.`, err);
 * }
 *
 * @see packages/web/app/routes/remix-api.log.ts
 * @see packages/web/app/entry.client.tsx
 */
export const createRemixClientLogger = (): Logger => {
    // The logger is instantiated outside the remix context
    // so it's just a simple fetch call
    const sendRequest = (
        method: keyof LoggerMethods,
        // This is just used as a reference to satisify the return type
        loggerRef: Logger,
    ): LeveledLogMethod => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return (message: string, ...meta: any[]): Logger => {
            try {
                if (!shouldSendReport(method, message, meta)) {
                    return loggerRef;
                }
                const formData = new FormData();
                formData.append('method', method);
                formData.append('message', message);
                formData.append(
                    'meta',
                    JSON.stringify({
                        ...meta
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            .map((value: any) => {
                                if (value instanceof Error) {
                                    // Serialize the error object
                                    const errorWithExtraProps: ErrorWithExtraBrowserProps =
                                        value;
                                    const errorObj = {
                                        message: errorWithExtraProps?.message,
                                        stack: errorWithExtraProps?.stack,
                                        name: errorWithExtraProps?.name,
                                        cause: errorWithExtraProps?.cause,
                                        fileName:
                                            errorWithExtraProps?.fileName ||
                                            errorWithExtraProps?.sourceURL,
                                        lineNumber:
                                            errorWithExtraProps?.lineNumber ||
                                            errorWithExtraProps?.line,
                                        columnNumber:
                                            errorWithExtraProps?.columnNumber ||
                                            errorWithExtraProps?.column,
                                        code: errorWithExtraProps?.code,
                                    };
                                    return {
                                        error: errorObj,
                                    };
                                }
                                return value;
                            })
                            .reduce((prev, curr) => {
                                return { ...prev, ...curr };
                            }, {}),
                        component: 'remix-client',
                        url:
                            typeof window !== 'undefined'
                                ? window.location.href
                                : undefined,
                        useragent:
                            typeof navigator !== 'undefined'
                                ? navigator.userAgent
                                : undefined,
                        referer:
                            typeof document !== 'undefined'
                                ? document.referrer
                                : undefined,
                    }),
                );

                sendLog(formData);
            } catch (err) {
                // Don't let client logging errors disrupt the application
                console.warn('Error sending log:', err);
            }
            return loggerRef;
        };
    };

    return logMethods.reduce((acc, method) => {
        acc[method] = sendRequest(method, acc);
        return acc;
    }, {} as LoggerMethods);
};

export const createMockLogger = (): Logger => {
    // The logger is instantiated outside the remix context
    // so it's just a simple fetch call
    const sendRequest = (
        method: keyof LoggerMethods,
        // This is just used as a reference to satisify the return type
        loggerRef: Logger,
    ): LeveledLogMethod => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return (message: string, ...meta: any[]): Logger => {
            console.log('Mock logger', method, message, meta);
            return loggerRef;
        };
    };

    return logMethods.reduce((acc, method) => {
        acc[method] = sendRequest(method, acc);
        return acc;
    }, {} as LoggerMethods);
};

const sendLog = async (body: FormData) => {
    try {
        const response = await fetch('/remix-api/log', {
            method: 'POST',
            body: body,
        });

        if (!response.ok) {
            console.warn(`Log endpoint returned status ${response.status}`);
        }
    } catch {
        // Silent failure - client logging should never break the application
        return;
    }
};
